import { useState, useEffect, useContext, createContext, useCallback, useMemo } from 'react';
import { authAPI } from '../api';
import { User, Usage, PlanLimits, Subscription } from '../types';

interface AuthContextType {
  user: User | null;
  usage: Usage | null;
  limits: PlanLimits | null;
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  isCustomAuthEnabled: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUsage: (usage: Usage, limits: PlanLimits) => void;
  navigateAfterAuth: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: any }) => {
  const [user, setUser] = useState<User | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setErrorCallback = useCallback((error: string | null) => {
    setError(error);
  }, []);

  // ✅ Always custom auth
  const isCustomAuthEnabled = true;

  // Verify token with backend
  const verifyToken = useCallback(async (token: string, isInitialLoad: boolean = false) => {
    try {
      const response = await authAPI.verifyToken(token);
      setUser(response.user);
      setUsage(response.usage);
      setLimits(response.limits);
      setSubscription(response.subscription);
      localStorage.setItem('user', JSON.stringify(response.user));
      setError(null);
    } catch (err: any) {
      console.error('Token verification failed:', err);
      if (!isInitialLoad) {
        setError(err.response?.data?.message || 'Authentication failed');
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  }, []);

  // Refresh user
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.user);
      setUsage(response.usage);
      setLimits(response.limits);
      setSubscription(response.subscription);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (err: any) {
      console.error('Failed to refresh user:', err);
      setError(err.response?.data?.message || 'Failed to refresh user data');
    }
  }, []);

  // Sign in
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.login(email, password);

      if (response.success && response.token) {
        localStorage.setItem('auth_token', response.token);
        setUser(response.user);
        setUsage(response.usage);
        setLimits(response.limits);
        setSubscription(response.subscription);
        localStorage.setItem('user', JSON.stringify(response.user));
        setError(null);
      }
    } catch (err: any) {
      let errorMessage = err.response?.data?.message || err.message || 'Sign in failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign up
// Sign up
// Sign up
const signUp = useCallback(
  async (email: string, password: string, name: string) => {
    try {
      setLoading(true);
      setError(null);

      // 1️⃣ Register user first
      const response = await authAPI.register(email, password, name);

      if (response.success) {
        // 2️⃣ Now immediately sign in using same credentials
        const loginResponse = await authAPI.login(email, password);

        if (loginResponse.success && loginResponse.token) {
          // ✅ Save token and user info
          localStorage.setItem("auth_token", loginResponse.token);
          setUser(loginResponse.user);
          setUsage(loginResponse.usage);
          setLimits(loginResponse.limits);
          setSubscription(loginResponse.subscription);
          localStorage.setItem("user", JSON.stringify(loginResponse.user));

          // ✅ Verify token once for consistency
          await verifyToken(loginResponse.token);

          // ✅ Redirect after login
          window.location.href = "/dashboard"; // or navigate("/dashboard")
        } else {
          setError("Auto-login failed after signup.");
        }
      } else {
        setError( "Signup failed.");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Sign up failed";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  },
  [verifyToken]
);



  // Sign out
  const signOut = useCallback(async () => {
    try {
      setLoading(true);

      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');

      setUser(null);
      setUsage(null);
      setLimits(null);
      setSubscription(null);
      setError(null);

      window.dispatchEvent(new CustomEvent('userLoggedOut'));
    } catch (err: any) {
      console.error('Sign out failed:', err);
      setError(err.message || 'Sign out failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Forgot password
  const forgotPassword = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.forgotPassword(email);
      if (!response.success) {
        setError(response.message || 'Failed to send password reset email');
      }
    } catch (err: any) {
      console.error('Forgot password failed:', err);
      setError(err.response?.data?.message || 'Failed to send password reset email');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (token: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.resetPassword(token, password);
      if (!response.success) {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err: any) {
      console.error('Reset password failed:', err);
      setError(err.response?.data?.message || 'Failed to reset password');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Restore session on load
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('auth_token');

    if (savedUser && token) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        verifyToken(token, true);
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, [verifyToken]);

  // Update usage
  const updateUsage = useCallback((newUsage: Usage, newLimits: PlanLimits) => {
    setUsage(newUsage);
    setLimits(newLimits);
  }, []);

  const memoizedUser = useMemo(() => user, [user]);

  const value: AuthContextType = {
    user: memoizedUser,
    usage,
    limits,
    subscription,
    loading,
    error,
    setError: setErrorCallback,
    isCustomAuthEnabled,
    signIn,
    signUp,
    signOut,
    refreshUser,
    updateUsage,
    navigateAfterAuth: useCallback(() => {}, []),
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
